/* eslint-disable */

import cn from '../../utils/cn';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import Pagination from 'rc-pagination';
import { Fragment, useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import Checkbox from '../Checkbox/Checkbox';
import Dropdown from '../Dropdown/Dropdown';
import DragDropIcon from '../Icons/DragDrop';
import Info from '../Icons/Info';
import TriangleDownIcon from '../Icons/TriangleDown';
import TriangeLeftIcon from '../Icons/TriangleLeft';
import TriangleRightIcon from '../Icons/TriangleRight';
import Text from '../Text/Text';
import Tooltip from '../Tooltip/Tooltip';

/** TABLE STYLING MAGIC
 * add background-color and border
 * 		border on row
 * 		bg-color must be on table cells, not row
 * 		AND cell must have 'relative' so the tr.border will render above td/th.border
 * */

const BORDER_CLASSNAME = 'border-b border-gray-200 relative ';
const NOTLAST_BORDER_CLASSNAME =
	'not-last:border-b not-last:border-gray-200 not-last:relative';

export const CELL_PADDING_CLASSNAME = 'p-3';

export const CHILD_ROWS_DEFAULT_STYLING = {
	childRowClassName:
		'bg-gray-100 hover:bg-[#eeeff2] transition-all duration-300 ease-out border-y border-gray-200',
	fontClassName: 'text-right text-ca-black text-xs',
};

const DragDropCell = ({
	disabled,
	onMouseEnter,
	onMouseLeave,
	key,
	dragRef,
}) => {
	return (
		<td
			key={key}
			className={cn(
				'p-0  cursor-grab  transition-all relative w-7 bg-gray-100 ',
				disabled
					? 'bg-ca-gray-200 group-hover/row:hover:bg-ca-gray-300 cursor-not-allowed'
					: `bg-ca-gray-200 group-hover/row:bg-ca-purple group-hover/row:hover:bg-purple-200 group-hover/row:bg-opacity-10 group-active/row:bg-ca-purple group-active/row:bg-opacity-10`,
				'text-ca-gray-400 group-hover/row:text-ca-purple group-hover/row:text-opacity-30 '
			)}
			ref={dragRef}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="flex justify-center items-stretch ">
				{!disabled && <DragDropIcon className="w-2.5" />}
			</div>
		</td>
	);
};

const TableRow = ({
	headings,
	index,
	row,
	rowKey,
	disabled,
	selected,
	selectable,
	loading,
	handleCheckedChanged,
	handleRowMovedPreview,
	handleRowMoved,
	renderCell,
	dragDisabled,
	dndType,
	dndAccepts,
	handleClick,
	handleMouseOver,
	handleMouseLeave,
}) => {
	const ref = useRef(null);
	const [canDrag, setCanDrag] = useState(false);
	const [{ handlerId }, drop] = useDrop({
		accept: dndAccepts ?? 'TABLE_ROW',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover: (item, monitor) => {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			// dont allow dragging of items that have their index present in `dragDisabled`
			if (
				dragDisabled.includes(dragIndex) ||
				dragDisabled.includes(hoverIndex)
			) {
				return;
			}

			handleRowMovedPreview(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			// eslint-disable-next-line no-param-reassign
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: dndType ?? 'TABLE_ROW',
		canDrag: canDrag && !dragDisabled.includes(index),
		item: () => {
			return { id: row?.[rowKey], index };
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
		end: handleRowMoved,
	});

	const isSelectable = selectable && !loading;
	drag(drop(ref));
	return (
		<tr
			ref={ref}
			className={cn(
				'relative transition-colors group/row',
				NOTLAST_BORDER_CLASSNAME,
				disabled.includes(row?.[rowKey]) && 'opacity-50 cursor-not-allowed',
				isDragging && 'opacity-50 bg-purple-50 cursor-grabbing bg-opacity-20',
				handleClick && !isDragging && 'cursor-pointer'
			)}
			onClick={handleClick ? (event) => handleClick(row, event) : undefined}
			onMouseOver={handleMouseOver ? () => handleMouseOver(row) : undefined}
			onMouseLeave={handleMouseLeave ? () => handleMouseLeave(row) : undefined}
			onFocus={handleMouseOver ? () => handleMouseOver(row) : undefined}
			data-handler-id={handlerId}
			key={`${row?.[rowKey]}`}
		>
			{isSelectable && (
				<td className="pl-5 pr-3" key={`selectable${row?.[rowKey]}`}>
					<Checkbox
						disabled={disabled.includes(row?.[rowKey])}
						checked={selected.includes(row?.[rowKey])}
						onChange={(checked) => handleCheckedChanged(row?.[rowKey], checked)}
					/>
				</td>
			)}
			{headings.map((col, colIndex) => {
				if (col.id === 'dragdrop') {
					return (
						<DragDropCell
							disabled={dragDisabled.includes(index)}
							key={`${row?.[rowKey]}_dragdrop`}
							onMouseEnter={() => setCanDrag(true)}
							onMouseLeave={() => setCanDrag(false)}
							dragRef={drag}
						/>
					);
				}

				return (
					<td
						className={cn(
							`text-sm text-ca-black leading-none whitespace-nowrap select-text relative transition-colors`,
							CELL_PADDING_CLASSNAME,
							handleClick
								? `group-hover/row:bg-purple-50`
								: 'group-hover/row:bg-gray-50',
							{
								'text-left': col?.align === 'left' || !col?.align,
								'text-right': col?.align === 'right',
								'text-center': col?.align === 'center',
							},
							colIndex === 0 && !isSelectable && 'pl-5',
							colIndex === headings.length - 1 && 'pr-5'
						)}
						key={`${row?.[rowKey]}_${col.id}`}
					>
						{renderCell(row, col.id, index)}
					</td>
				);
			})}
		</tr>
	);
};

TableRow.propTypes = {
	headings: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			label: PropTypes.string,
			tooltip: PropTypes.string,
			sortable: PropTypes.bool,
			align: PropTypes.oneOf(['left', 'center', 'right']),
		})
	),
	index: PropTypes.number.isRequired,
	row: PropTypes.any,
	rowKey: PropTypes.string,
	renderCell: PropTypes.func,
	handleCheckedChanged: PropTypes.func,
	handleRowMoved: PropTypes.func,
	handleRowMovedPreview: PropTypes.func,
	handleClick: PropTypes.func,
	handleMouseOver: PropTypes.func,
	disabled: PropTypes.array,
	selected: PropTypes.array,
	loading: PropTypes.bool,
	selectable: PropTypes.bool,
	dragDisabled: PropTypes.arrayOf(PropTypes.number),
	/**
	 * What the item type is of this row in context of the drag and drop system.
	 */
	dndType: PropTypes.string,
	/**
	 * What item type is accepted to be dropped at this row in context of the drag and drop system.
	 */
	dndAccepts: PropTypes.arrayOf(PropTypes.string),
};

TableRow.defaultProps = {
	headings: [],
	row: {},
	rowKey: 'id',
	renderCell: (row, columnId) => {
		row?.[columnId]?.toString();
	},
	selectable: false,
	selected: [],
	handleCheckedChanged: () => { },
	handleRowMoved: () => { },
	handleRowMovedPreview: () => { },
	disabled: [],
	loading: false,
	dragDisabled: [],
};

const Table = ({
	className,
	headings,
	rows: sourceRows,
	rowKey,
	dndRowTypes,
	dndRowAccepts,
	separatorRows,
	renderCell,
	pagination,
	onPageChange,
	onItemsPerPageChange,
	sort,
	onSortChange,
	selectable,
	selected,
	onSelectedChange,
	onDragChange,
	onRowClick,
	onRowMouseOver,
	onRowMouseLeave,
	disabled,
	dragDisabled,
	open,
	renderOpen,
	renderOpenTr,
	loading,
	itemsLoading,
	emptyState,
	isInline,
	renderAfterRow,
	maxHeightTable,
}) => {
	const [rows, setRows] = useState([]);
	const allChecked = !rows.find((row) => !selected.includes(row?.[rowKey]));

	useEffect(() => {
		setRows(() => sourceRows);
	}, [sourceRows]);

	const handleCheckedChanged = (id, checked) => {
		if (checked) {
			onSelectedChange([...selected, id]);
		} else {
			onSelectedChange(selected.filter((rId) => rId !== id));
		}
	};

	const handleCheckAll = () => {
		if (allChecked) {
			onSelectedChange(
				selected.filter((s) => !rows.find((row) => row?.[rowKey] === s))
			);
		} else {
			onSelectedChange(
				[...selected, ...rows.map((row) => row?.[rowKey])]
					.filter(
						// filter out disabled rows
						(value) => !disabled.includes(value)
					)
					.filter(
						// filter out duplicates
						(value, index, self) => self.indexOf(value) === index
					)
			);
		}
	};

	const handleRowMoved = () => {
		onDragChange(rows?.map(({ [rowKey]: rKey }) => rKey));
	};

	const handleRowMovedPreview = (dragIndex, hoverIndex) => {
		const updatedRows = update(rows, {
			$splice: [
				[dragIndex, 1],
				[hoverIndex, 0, rows[dragIndex]],
			],
		});
		setRows(updatedRows);
	};

	if (!headings.length) return null;

	const isSelectable = selectable && !loading;

	return (
		<DndProvider backend={HTML5Backend} context={window}>
			<div
				className={cn(
					'relative bg-white shadow-xl',
					!isInline ? 'rounded-lg shadow-ca-hack-silver overflow-hidden' : ''
				)}
			>
				<div className={(cn('overflow-x-auto'), maxHeightTable)}>
					<table className={cn('w-full table-auto relative', className)}>
						<thead className="relative after:content[''] after:absolute after:h-[1px] after:bg-ca-silver after:bottom-0 after:right-0 after:left-0 after:z-20">
							<tr
								className={cn(
									'w-full bg-white z-10 top-0 sticky',
									BORDER_CLASSNAME
								)}
							>
								{isSelectable && (
									<th className="pl-5 pr-3 relative" key="check">
										<Checkbox checked={allChecked} onChange={handleCheckAll} />
									</th>
								)}
								{headings.map((col, colIndex) => {
									if (col.id === 'dragdrop') {
										return (
											<th
												key={col.id}
												className={cn(
													'bg-ca-ghost-white relative',
													col?.maxWidth && `w-[${col?.maxWidth}]`
												)}
												style={{ maxWidth: col?.maxWidth ?? 'unset' }}
											></th>
										);
									}

									return (
										<th
											key={col.id}
											className={cn(
												'p-3 font-bold text-xs text-ca-black leading-none ',
												{
													'text-left': col?.align === 'left' || !col?.align,
													'text-right': col?.align === 'right',
													'text-center': col?.align === 'center',
												},
												colIndex === 0 && !isSelectable && 'pl-5',
												colIndex === headings.length - 1 && 'pr-5',
												col?.sortable && 'cursor-pointer',
												col.className
											)}
											onClick={() => {
												if (!col?.sortable) return;

												if (col?.id === sort?.key) {
													onSortChange({
														key: col?.id,
														direction:
															sort?.direction === 'asc' ? 'desc' : 'asc',
													});
												} else {
													onSortChange({
														key: col?.id,
														direction: 'desc',
													});
												}
											}}
											style={{
												maxWidth: col?.maxWidth ?? 'unset',
											}}
										>
											<Tooltip content={col?.tooltip}>
												<span className="inline-flex gap-2 items-center">
													{col?.label}
													{col?.tooltip && (
														<Info className="h-4 text-gray-400" />
													)}
												</span>
											</Tooltip>
											{col?.id === sort?.key && (
												<TriangleDownIcon
													className={cn(
														'inline ml-2 w-2.5 text-ca-gray',
														sort?.direction === 'asc' && 'transform rotate-180'
													)}
												/>
											)}
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody className="min-h-12 ">
							{!loading && !rows.length && emptyState ? (
								<tr className="relative">
									<td className="text-center" colSpan="1000">
										<Text className="py-24">{emptyState}</Text>
									</td>
								</tr>
							) : null}
							{!loading && (!!rows.length || emptyState)
								? rows.map((row, index) => (
									<Fragment key={`fragement${row?.[rowKey]}`}>
										{(() => {
											const separators = separatorRows.filter(
												(separatorHay) => separatorHay.position === index
											);
											if (!separators) {
												return null;
											}
											return separators.map((separator) => (
												<tr
													key={`separator${separator.key}`}
													className={cn(
														' text-gray-500 text-xs   transition-colors',
														BORDER_CLASSNAME,
														separator.className
													)}
												>
													{separator.colStart !== undefined ||
														(separator.colStart > 0 && (
															<td key="spacer" colSpan={separator.colStart} />
														))}
													<td
														key="content"
														colSpan={separator.colSpan ?? 1000}
														className="py-1 px-10 bg-gray-100 relative hover:bg-gray-200"
													>
														{separator.renderSeparatorRow()}
													</td>
												</tr>
											));
										})()}

										<Fragment key={row?.[rowKey]}>
											<TableRow
												index={index}
												row={row}
												rowKey={rowKey}
												headings={headings}
												disabled={disabled}
												selected={selected}
												open={open}
												selectable={selectable}
												loading={loading}
												handleCheckedChanged={handleCheckedChanged}
												handleClick={onRowClick}
												handleMouseOver={onRowMouseOver}
												handleMouseLeave={onRowMouseLeave}
												renderCell={renderCell}
												renderOpen={renderOpen}
												handleRowMovedPreview={handleRowMovedPreview}
												handleRowMoved={handleRowMoved}
												dragDisabled={dragDisabled}
												dndType={dndRowTypes && dndRowTypes[index]}
												dndAccepts={dndRowTypes && dndRowAccepts[index]}
											/>
											{(() => {
												const isOpen = open?.includes(row?.[rowKey]);
												if (!isOpen) {
													return null;
												}
												if (!renderOpenTr) {
													return (
														<tr
															className={cn('relative')}
															key={`${row?.[rowKey]}open${Date.now()}`}
														>
															<td
																colSpan={
																	headings.length + (selectable ? 1 : 0)
																}
															>
																{renderOpen(row?.[rowKey])}
															</td>
														</tr>
													);
												}
												return renderOpenTr(row, (...args) =>
													renderOpen(args)
												);
											})()}
											{renderAfterRow ? renderAfterRow({ row, index }) : null}
										</Fragment>
									</Fragment>
								))
								: null}
							{loading &&
								Array(itemsLoading)
									.fill(0)
									.map((i, index) => (
										// eslint-disable-next-line react/no-array-index-key
										<tr
											key={`skeleton_loader_${index}`}
											className="border-b border-ca-silver relative"
										>
											{headings.map((col, colIndex) => {
												if (col.id === 'dragdrop') {
													return (
														<DragDropCell
															disabled={true}
															key={`skeleton_loader_${index}_${col.id}`}
														/>
													);
												}

												return (
													<td
														className={cn(
															CELL_PADDING_CLASSNAME,
															colIndex === 0 && 'pl-5',
															colIndex === headings.length - 1 && 'pr-5'
														)}
														// eslint-disable-next-line react/no-array-index-key
														key={`skeleton_loader_${index}_${col.id}`}
													>
														<div className="w-full h-6 bg-ca-silver rounded-full animate-pulse" />
													</td>
												);
											})}
										</tr>
									))}
						</tbody>
					</table>
				</div>
				{!!Object.keys(pagination).length && (
					<div className="flex justify-between border-t border-ca-silver">
						<div className="flex items-center space-x-2 ml-2">
							{pagination?.itemsPerPageOptions && (
								<>
									<Dropdown
										onChange={onItemsPerPageChange}
										value={pagination.itemsPerPage}
										options={pagination.itemsPerPageOptions.map((n) => ({
											value: n,
											label: n,
										}))}
										size="small"
										dropUpDown="up"
									/>
									<Text type="secondary" size="text-xs">
										results per page
									</Text>
								</>
							)}
						</div>
						<div className="flex divide-x divide-x-ca-silver">
							<Pagination
								className="flex items-center mr-2.5"
								current={pagination.currentPage}
								total={pagination.items}
								pageSize={pagination.itemsPerPage}
								onChange={onPageChange}
								locale="en"
								itemRender={(current, type) => {
									if (
										type === 'page' ||
										type === 'jump-prev' ||
										type === 'jump-next'
									) {
										return (
											<button
												key={current}
												type="button"
												className={cn(
													'py-0.5 px-2 mx-1 text-xs tabular-nums cursor-pointer rounded transition-colors',
													current === pagination.currentPage
														? 'font-bold text-white bg-ca-purple'
														: 'text-ca-black hover:bg-ca-silver'
												)}
											>
												{type === 'page' ? current : '...'}
											</button>
										);
									}

									return null;
								}}
							/>
							<button
								type="button"
								onClick={() =>
									onPageChange(Math.max(pagination.currentPage - 1, 1))
								}
								className={cn(
									'p-4 text-ca-gray transition-colors',
									pagination.currentPage === 1
										? 'cursor-not-allowed'
										: 'hover:text-ca-black'
								)}
								disabled={pagination.currentPage === 1}
							>
								<TriangeLeftIcon className="w-2" />
							</button>
							<button
								type="button"
								onClick={() =>
									onPageChange(
										Math.min(
											pagination.currentPage + 1,
											Math.ceil(pagination.items / pagination.itemsPerPage)
										)
									)
								}
								className={cn(
									'p-4 text-ca-gray',
									pagination.currentPage ===
										Math.ceil(pagination.items / pagination.itemsPerPage)
										? 'cursor-not-allowed'
										: 'hover:text-ca-black'
								)}
								disabled={
									pagination.currentPage ===
									Math.ceil(pagination.items / pagination.itemsPerPage)
								}
							>
								<TriangleRightIcon className="w-2" />
							</button>
						</div>
					</div>
				)}
			</div>
		</DndProvider>
	);
};

Table.propTypes = {
	className: PropTypes.string,
	headings: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			label: PropTypes.string,
			tooltip: PropTypes.string,
			sortable: PropTypes.bool,
			align: PropTypes.oneOf(['left', 'center', 'right']),
			maxWidth: PropTypes.string,
		})
	),
	rows: PropTypes.arrayOf(PropTypes.any),
	rowKey: PropTypes.string,
	/**
	 * [Optional]
	 * Dnd `type` per row
	 * If empty, dnd enabled everywhere
	 */
	dndRowTypes: PropTypes.arrayOf(PropTypes.string),
	/**
	 * [Optional]
	 * Dnd `accepts` types per row
	 * If empty, dnd enabled everywhere
	 */
	dndRowAccepts: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),

	/**
	 * Insert rows at position which you can fully render.
	 */
	separatorRows: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			position: PropTypes.number.isRequired,
			colSpan: PropTypes.number,
			colStart: PropTypes.number,
			className: PropTypes.string, // className for the <tr/> container element
			renderSeparatorRow: PropTypes.func.isRequired,
		})
	),
	renderCell: PropTypes.func,
	renderAfterRow: PropTypes.func,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		items: PropTypes.number,
		itemsPerPage: PropTypes.number,
		itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
	}),
	onPageChange: PropTypes.func,
	onItemsPerPageChange: PropTypes.func,
	onRowClick: PropTypes.func,
	onRowMouseOver: PropTypes.func,
	onRowMouseLeave: PropTypes.func,
	sort: PropTypes.shape({
		key: PropTypes.string,
		direction: PropTypes.oneOf(['asc', 'desc']),
	}),
	onSortChange: PropTypes.func,
	selectable: PropTypes.bool,
	selected: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	onSelectedChange: PropTypes.func,
	onDragChange: PropTypes.func,
	dragDisabled: PropTypes.arrayOf(PropTypes.number),
	disabled: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	open: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	renderOpen: PropTypes.func,
	/**
	 * Override for default render behaviour of open part of an 'open' row.
	 * pass a function here which receives the row which is open as parameter.
	 */
	renderOpenTr: PropTypes.func,
	loading: PropTypes.bool,
	itemsLoading: PropTypes.number,
	emptyState: PropTypes.string,
	isInline: PropTypes.bool,
	maxHeightTable: PropTypes.string,
};

Table.defaultProps = {
	headings: [],
	rows: [],
	rowKey: 'id',
	separatorRows: [],
	renderCell: (row, columnId) => {
		row?.[columnId]?.toString();
	},
	pagination: {},
	onPageChange: () => { },
	onItemsPerPageChange: () => { },
	sort: {},
	onSortChange: () => { },
	selectable: false,
	selected: [],
	onSelectedChange: () => { },
	onDragChange: () => { },
	dragDisabled: [],
	disabled: [],
	open: [],
	renderOpen: null,
	renderOpenTr: () => undefined,
	loading: false,
	itemsLoading: 10,
	emptyState: '',
	isInline: false,
	maxHeightTable: '',
};

export default Table;
